import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider,
  Button,
} from 'components/mui';
import { CheckIcon, ClearIcon } from 'components/icons';
import PrintIcon from '@mui/icons-material/Print';
import { Case } from 'containers/pages/cases/types';

interface CaseSummaryProps {
  caseData: Case;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
      {title}
    </Typography>
  );
}

function SummaryField({ label, value }: { label: string; value: string | number }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{value || 'N/A'}</Typography>
    </Box>
  );
}

function BooleanFlag({ label, value }: { label: string; value: boolean }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
      {value ? (
        <CheckIcon fontSize="small" color="success" />
      ) : (
        <ClearIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      )}
      <Typography variant="body2" color={value ? 'success.main' : 'text.secondary'}>
        {label}: {value ? 'Yes' : 'No'}
      </Typography>
    </Box>
  );
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'N/A';
  try {
    // Handle .NET /Date(ms)/ format
    const dotNetMatch = /\/Date\((-?\d+)\)\//.exec(dateStr);
    const d = dotNetMatch
      ? new Date(parseInt(dotNetMatch[1], 10))
      : new Date(dateStr);
    return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
  } catch {
    return 'N/A';
  }
}

function fixRelativeImagePaths(html: string): string {
  return html.replace(
    /src="(?!https?:\/\/|\/)(.*?)"/gi,
    'src="/Portal/$1"',
  );
}

function CaseSummary({ caseData }: CaseSummaryProps) {
  const handlePrint = () => {
    window.print();
  };

  const handlePrintPdf = () => {
    window.open(`/canappsv3/cantrack/cases/${caseData.Id}/print`, '_blank');
  };

  return (
    <Paper sx={{ p: 3, '@media print': { boxShadow: 'none', p: 1 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          {caseData.Title} (#{caseData.Id})
        </Typography>
        <Chip label={caseData.StatusTitle} size="small" color="primary" variant="outlined" sx={{ mt: 0.5 }} />
        {caseData.StatusComment && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {caseData.StatusComment}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Contacts & GPD */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SectionHeader title="Contacts" />
          <SummaryField label="Customer" value={caseData.CustomerName} />
          <SummaryField label="Consultant" value={caseData.ConsultantName} />
          <SummaryField label="Primary Contact" value={caseData.PrimaryContactName} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SectionHeader title="Group / Project / Deliverable" />
          <SummaryField label="Group" value={caseData.GroupTitle} />
          <SummaryField label="Project" value={caseData.ProjectTitle} />
          <SummaryField label="Deliverable" value={caseData.DeliverableTitle} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Activity */}
      <SectionHeader title="Activity" />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <SummaryField label="Status" value={caseData.StatusTitle} />
          <SummaryField label="Time Spent" value={caseData.TimeSpent || '0'} />
          <SummaryField label="Number of Activities" value={caseData.NumberOfActivities} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SummaryField label="Created" value={formatDate(caseData.CreatedOn)} />
          <SummaryField label="Last Update" value={formatDate(caseData.UpdatedOn)} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Description */}
      <SectionHeader title="Description" />
      {caseData.Description ? (
        <Box
          sx={{ '& p': { m: 0 }, '& img': { maxWidth: '100%' }, typography: 'body2', mb: 2 }}
          dangerouslySetInnerHTML={{ __html: fixRelativeImagePaths(caseData.Description) }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No description
        </Typography>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Keywords & References */}
      <SectionHeader title="Keywords" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {caseData.Keywords?.length > 0 ? (
          caseData.Keywords.map((kw) => <Chip key={kw.Id} label={kw.Title} size="small" />)
        ) : (
          <Typography variant="body2" color="text.secondary">None</Typography>
        )}
      </Box>
      <SectionHeader title="References" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
        {caseData.References?.length > 0 ? (
          caseData.References.map((ref) => <Chip key={ref.Id} label={ref.Title} size="small" variant="outlined" />)
        ) : (
          <Typography variant="body2" color="text.secondary">None</Typography>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Config */}
      <SectionHeader title="Configuration" />
      <BooleanFlag label="Private" value={caseData.Private} />
      <BooleanFlag label="IP Restricted" value={caseData.IpRestricted} />

      <Divider sx={{ my: 2 }} />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, '@media print': { display: 'none' } }}>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrintPdf}>
          Print PDF
        </Button>
        <Button variant="outlined" onClick={handlePrint}>
          Browser Print
        </Button>
      </Box>
    </Paper>
  );
}

export default CaseSummary;
